import { PackageIcon } from '@phosphor-icons/react';
import {
  Alert,
  Badge,
  Button,
  Cell,
  Column,
  EmptyState,
  Row,
  RowExpandButton,
  Table,
  TableBody,
  TableContainer,
  TableHeader,
} from '@skupreme/shared/ui';
import { useState } from 'react';
import { useMappedItems } from '../../hooks/use-mapped-items';
import { mappingErrorMessage } from '../../lib/mapping-errors';
import type { MappedItem } from '../../lib/types';
import {
  ListingCell,
  ListPagination,
  MappedRowActions,
  placeholderRows,
  sourceFilter,
  TruncatedLine,
  useListingEdits,
} from '../MappableListingsTable';
import { MappingActions } from '../MappingActions';

export type MappedProductsTableProps = {
  /** The plan or role can't change listings: actions show, disabled. */
  isReadOnly?: boolean;
  /** The plan hides these actions altogether. */
  isActionsHidden?: boolean;
  /** Offers "Undo mapping" in a listing row's menu. */
  onUndo?: (listingId: string) => void;
  isUndoing?: boolean;
};

const COLUMN_COUNT = 3;

export const NO_WAREHOUSE_LABEL = 'No warehouse';

/* "1 warehouse", "3 marketplaces". */
export const countLabel = (count: number, noun: string) =>
  `${count.toLocaleString()} ${noun}${count === 1 ? '' : 's'}`;

/* What the product is linked to, in small badges; a product no warehouse stocks is a caution. */
function LinkCounts({ item }: { item: MappedItem }) {
  return (
    <div className="sk:flex sk:flex-wrap sk:items-center sk:gap-1.5">
      {item.marketplaceCount > 0 ? (
        <Badge>{countLabel(item.marketplaceCount, 'marketplace')}</Badge>
      ) : null}
      {item.warehouseCount > 0 ? (
        <Badge tone="info">
          {countLabel(item.warehouseCount, 'warehouse')}
        </Badge>
      ) : (
        <Badge tone="caution">{NO_WAREHOUSE_LABEL}</Badge>
      )}
    </div>
  );
}

/**
 * M2 (Ely 11-04): the Mapped tab by product. One row per product with what it
 * is linked to; expanded, a row per mapped listing with the same quiet actions
 * as the Listings view.
 */
export function MappedProductsTable({
  isReadOnly,
  isActionsHidden,
  onUndo,
  isUndoing,
}: MappedProductsTableProps) {
  const { data, loading, error, totalData, filter, filterChanged } =
    useMappedItems();
  const edits = useListingEdits();
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());

  const allExpanded =
    data.length > 0 && data.every(item => expanded.has(item.itemId));
  const toggle = (itemId: string, isOpen: boolean) =>
    setExpanded(previous => {
      const next = new Set(previous);
      if (isOpen) next.add(itemId);
      else next.delete(itemId);
      return next;
    });

  return (
    <div className="sk:flex sk:flex-col sk:gap-3">
      <div className="sk:flex sk:flex-wrap sk:items-center sk:gap-3">
        <MappingActions
          filters={[
            sourceFilter(filter.source, source =>
              filterChanged('source', source),
            ),
          ]}
          search={filter.search}
          onSearch={search => filterChanged('search', search)}
        />
        {data.length > 0 ? (
          <Button
            variant="ghost"
            size="sm"
            onPress={() =>
              setExpanded(
                allExpanded
                  ? new Set()
                  : new Set(data.map(item => item.itemId)),
              )
            }
          >
            {allExpanded ? 'Collapse all' : 'Expand all'}
          </Button>
        ) : null}
      </div>
      {error ? <Alert tone="danger">{mappingErrorMessage(error)}</Alert> : null}
      <TableContainer className="sk:overflow-visible sk:rounded-card sk:bg-surface">
        <Table aria-label="Products" className="sk:table-fixed">
          <TableHeader>
            <Column id="product" isRowHeader className="sk:w-[45%]">
              Product
            </Column>
            <Column id="links">Linked to</Column>
            <Column id="actions" aria-label="Actions" className="sk:w-[10rem]">
              {''}
            </Column>
          </TableHeader>
          <TableBody
            renderEmptyState={() => (
              <EmptyState
                icon={<PackageIcon size={20} />}
                title="No mapped products"
                description="Products with a listing mapped to them show here."
              />
            )}
          >
            {loading
              ? placeholderRows(COLUMN_COUNT)
              : data.flatMap(item => {
                  const isOpen = expanded.has(item.itemId);
                  return [
                    <Row key={item.itemId} id={`item-${item.itemId}`}>
                      <Cell>
                        <div className="sk:flex sk:min-w-0 sk:items-center sk:gap-2">
                          <RowExpandButton
                            isExpanded={isOpen}
                            onExpandedChange={open => toggle(item.itemId, open)}
                            aria-label={`${isOpen ? 'Collapse' : 'Expand'} ${item.itemTitle}`}
                          />
                          <div className="sk:flex sk:min-w-0 sk:flex-col">
                            <TruncatedLine
                              text={item.itemTitle}
                              className="sk:text-sm sk:font-semibold sk:text-fg"
                            />
                            {item.itemSku ? (
                              <TruncatedLine
                                text={item.itemSku}
                                className="sk:text-dense sk:text-fg-secondary"
                              />
                            ) : null}
                          </div>
                        </div>
                      </Cell>
                      <Cell>
                        <LinkCounts item={item} />
                      </Cell>
                      <Cell>{''}</Cell>
                    </Row>,
                    ...(isOpen ? item.listings : []).map(listing => (
                      <Row key={listing.id} id={listing.id} isNested>
                        <Cell>
                          <ListingCell listing={listing} />
                        </Cell>
                        <Cell>{''}</Cell>
                        <Cell>
                          {isActionsHidden ? null : (
                            <div className="sk:flex sk:flex-nowrap sk:items-center sk:justify-end sk:gap-2 sk:whitespace-nowrap">
                              <MappedRowActions
                                isReadOnly={isReadOnly}
                                isBusy={edits.isSaving}
                                isUndoing={isUndoing}
                                onRemap={() => edits.pick(listing)}
                                onExclude={() => edits.exclude(listing)}
                                onUndo={onUndo && (() => onUndo(listing.id))}
                              />
                            </div>
                          )}
                        </Cell>
                      </Row>
                    )),
                  ];
                })}
          </TableBody>
        </Table>
        <ListPagination
          filter={filter}
          total={totalData}
          onChange={filterChanged}
        />
      </TableContainer>
      {edits.modals}
    </div>
  );
}
